import { View } from 'react-native';
import { Slider } from '../slider/Slider';
import React, { useEffect, useState } from 'react';
import { useStore } from '../../../contexts/StoreContext';
import { observer } from 'mobx-react-lite';
import { calculateZoomValue } from 'src/helpers/zoom.helper';
import { MAX_ZOOM, MIN_ZOOM, STEP_ZOOM } from 'src/constants/zoom.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { sliderZoomTestID } from 'src/helpers/testIDs.helper';
import { IconButton } from '@photosi/rn-components/buttons/icon-button/IconButton';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { emit } from '../../../contexts/TransformEventBus';
import Animated, { FadeIn, FadeOut, runOnUI } from 'react-native-reanimated';
import { styles } from './SliderZoom.styles';
import { UserImageModelInstance } from 'src/store/_models/userImage';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { getZoomAnalyticsValues } from 'src/_shared/utils/getZoomAnalyticsValues';
import { parseAnalyticsParamsByUserImage } from 'src/_shared/analytics.utils';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';

export type SliderZoomProps = {
  customUserImage?: UserImageModelInstance;
  zoomStep?: number;
  onChange?: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  isDark?: boolean;
  focusGroup?: FocusGroups;
  // Offset applied to this instance's focusOrder, so multiple SliderZoom instances
  // rendered at once (e.g. one per grid tile) don't collide in the focus order.
  focusOrderBase?: number;
  // Prefix applied to this instance's focusId/testID, for the same reason.
  focusIdPrefix?: string;
};

/**
 * SliderZoom - Modalità Hybrid con TransformEventBus
 *
 * Componente per controllo zoom con slider e pulsanti +/-.
 * Ora integrato con TransformEventBus per comunicazione con provider locali.
 *
 * COMPORTAMENTO ESISTENTE: Mantiene tutta la logica originale invariata
 * - Slider per zoom continuo durante drag
 * - Pulsanti per zoom discreto ±step
 * - Callbacks onChange/onChangeEnd funzionano identicamente
 * - Calcoli min/max/step zoom inalterati
 *
 * NUOVO: TransformEventBus integration
 * - Emette eventi 'temp-zoom' durante drag slider
 * - Emette eventi 'apply-zoom' al termine delle operazioni
 * - Zero impatto sulla funzionalità esistente
 * - Preparazione per provider locali con eventi zoom
 *
 * La funzionalità è identica a prima, ma ora è pronta per il sistema eventi.
 */

const SliderZoomComponent = observer(
  ({
    customUserImage,
    zoomStep = STEP_ZOOM,
    onChange = () => {},
    onChangeEnd = () => {},
    isDark = false,
    focusGroup = FocusGroups.BEFORE_PRODUCT,
    focusOrderBase = 0,
    focusIdPrefix,
  }: SliderZoomProps) => {
    const {
      i18n,
      dynamicModal,
      project: { activeUserImageArea },
    } = useStore();
    const [isHoveredIncrease, setIsHoveredIncrease] = useState(false);
    const [isHoveredDecrease, setIsHoveredDecrease] = useState(false);

    const userImage = customUserImage ?? activeUserImageArea?.userImage;

    const minValue = userImage?.minScale ?? MIN_ZOOM;
    // const minRelativeValue = userImage?.minRelativeScale ?? minValue;
    const maxValue = userImage?.maxScale ?? MAX_ZOOM;
    // state is needed to disable buttons when max or min value is reached
    const [zoomValue, setZoomValue] = useState<number>(userImage?.scale ?? 1);

    useEffect(() => {
      if (userImage) setZoomValue(userImage.scale);
    }, [userImage?.scale]);

    if (!userImage) return null;

    const focusIdSegments = focusIdPrefix ? [focusIdPrefix] : [];

    return (
      <View style={[styles.container, isDark && { paddingHorizontal: 0 }]} testID={sliderZoomTestID()}>
        {/* ZOOM-OUT BUTTON */}
        <IconButton
          testID={sliderZoomTestID(...focusIdSegments, TestIDs.ZOOM_OUT_BUTTON)}
          disabled={zoomValue <= minValue}
          onPress={async () => {
            const prevZoomValue = zoomValue - zoomStep;
            let newZoomValue = calculateZoomValue(prevZoomValue, minValue, maxValue);
            if (newZoomValue < minValue) newZoomValue = minValue;
            // if (newZoomValue < minRelativeValue) newZoomValue = minRelativeValue;
            // userImage.applyStepScale(newZoomValue, responsiveScale);

            // NUOVO: Emit eventi per button controls (Main Thread)
            runOnUI(() => {
              emit({
                type: 'apply-zoom',
                userImageId: userImage.id,
                payload: { absoluteScale: newZoomValue },
                source: 'slider',
              });
            })();
            let contextAnalytics = {};
            if (dynamicModal.isModalVisible) {
              contextAnalytics = {
                context:
                  dynamicModal.activeModal.id === DYNAMIC_MODAL_VIEWS.EditProducts
                    ? 'photo_edit_view'
                    : 'review_cut_view',
              };
            }
            amplitude.trackEvent(AnalyticsEvents.modifyPhoto.zoomClick, {
              ...getZoomAnalyticsValues(zoomValue, newZoomValue),
              ...parseAnalyticsParamsByUserImage(userImage),
              type: 'button',
              ...contextAnalytics,
            });

            setZoomValue(newZoomValue);
            onChangeEnd(newZoomValue);
          }}
          accessible={true}
          accessibilityLabel={i18n.t(`actions:reduce_zoom`)}
          accessibilityRole="button"
          style={styles.buttonZoom}
          focusStyle={styles.buttonZoomFocus}
          focusId={sliderZoomTestID(...focusIdSegments, TestIDs.ZOOM_OUT_BUTTON)}
          focusOrder={focusOrderBase + 1}
          focusGroup={focusGroup}
          iconName={ICON_NAMES.ZoomMinus}
          iconStyle={[styles.icon, isDark && { color: '#252527' }]}
          onHoverIn={() => setIsHoveredDecrease(true)}
          onHoverOut={() => setIsHoveredDecrease(false)}
        />
        {isHoveredDecrease && (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(200)}
            style={styles.hoverTextContainer}
            pointerEvents="none"
          >
            <TextLato style={styles.hoverText}>{i18n.t(`actions:reduce_zoom`)}</TextLato>
          </Animated.View>
        )}
        {/* ZOOM SLIDER */}
        <Slider
          testID={sliderZoomTestID(...focusIdSegments, TestIDs.BAR)}
          isDark={isDark}
          minValue={minValue}
          maxValue={maxValue}
          defaultValue={zoomValue}
          focusId={sliderZoomTestID(...focusIdSegments, TestIDs.BAR)}
          customWidth={!!customUserImage}
          focusOrder={focusOrderBase + 2}
          focusGroup={focusGroup}
          onChange={value => {
            onChange(value);
            // userImage?.setScale(value, responsiveScale);

            // NUOVO: Emit evento temporaneo direttamente da worklet (UI thread)
            if (userImage) {
              emit({
                type: 'temp-zoom',
                userImageId: userImage.id,
                payload: { absoluteScale: value },
                source: 'slider',
              });
            }
          }}
          onChangeEnd={value => {
            // userImage?.applyScale(value, responsiveScale);

            // NUOVO: Emit evento apply direttamente da worklet (UI thread)
            if (userImage) {
              emit({
                type: 'apply-zoom',
                userImageId: userImage.id,
                payload: { absoluteScale: value },
                source: 'slider',
              });
            }
            let contextAnalytics = {};
            if (dynamicModal.isModalVisible) {
              contextAnalytics = {
                context:
                  dynamicModal.activeModal.id === DYNAMIC_MODAL_VIEWS.EditProducts
                    ? 'photo_edit_view'
                    : 'review_cut_view',
              };
            }
            amplitude.trackEvent(AnalyticsEvents.modifyPhoto.zoomClick, {
              ...getZoomAnalyticsValues(zoomValue, value),
              ...parseAnalyticsParamsByUserImage(userImage),
              type: 'slider',
              ...contextAnalytics,
            });

            setZoomValue(value);
            onChangeEnd(value);
          }}
        />
        {/* ZOOM-IN BUTTON */}
        <IconButton
          style={styles.buttonZoom}
          focusStyle={styles.buttonZoomFocus}
          testID={sliderZoomTestID(...focusIdSegments, TestIDs.ZOOM_IN_BUTTON)}
          disabled={zoomValue >= maxValue}
          onPress={async () => {
            const nextZoomValue = zoomValue + zoomStep;
            let newZoomValue = calculateZoomValue(nextZoomValue, minValue, maxValue);
            if (newZoomValue > maxValue) newZoomValue = maxValue;
            // userImage.applyStepScale(newZoomValue, responsiveScale);

            // NUOVO: Emit eventi per button controls (Main Thread)
            runOnUI(() => {
              emit({
                type: 'apply-zoom',
                userImageId: userImage.id,
                payload: { absoluteScale: newZoomValue },
                source: 'slider',
              });
            })();
            let contextAnalytics = {};
            if (dynamicModal.isModalVisible) {
              contextAnalytics = {
                context:
                  dynamicModal.activeModal.id === DYNAMIC_MODAL_VIEWS.EditProducts
                    ? 'photo_edit_view'
                    : 'review_cut_view',
              };
            }
            amplitude.trackEvent(AnalyticsEvents.modifyPhoto.zoomClick, {
              ...getZoomAnalyticsValues(zoomValue, newZoomValue),
              ...parseAnalyticsParamsByUserImage(userImage),
              type: 'button',
              ...contextAnalytics,
            });

            setZoomValue(newZoomValue);
            onChangeEnd(newZoomValue);
          }}
          accessible={true}
          accessibilityLabel={i18n.t(`actions:increase_zoom`)}
          accessibilityRole="button"
          focusId={sliderZoomTestID(...focusIdSegments, TestIDs.ZOOM_IN_BUTTON)}
          focusOrder={focusOrderBase + 3}
          focusGroup={focusGroup}
          iconName={ICON_NAMES.ZoomPlus}
          iconStyle={[styles.icon, isDark && { color: '#252527' }]}
          onHoverIn={() => setIsHoveredIncrease(true)}
          onHoverOut={() => setIsHoveredIncrease(false)}
        />
        {isHoveredIncrease && (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOut.duration(200)}
            style={styles.hoverTextContainerRight}
            pointerEvents="none"
          >
            <TextLato style={styles.hoverText}>{i18n.t(`actions:increase_zoom`)}</TextLato>
          </Animated.View>
        )}
      </View>
    );
  },
);

export const SliderZoom = SliderZoomComponent;
